"use client"

import React, { useEffect, useState } from 'react'
import styles from './styles.module.scss'
import { useCategory } from '@/src/context/category/category-context'
import SvgDots from '@/src/assets/svgs/collection/dots.svg'
import { CategoriesFunctions } from '../functions'
import Category from '@/src/interfaces/catalog/category'
import BaseLoading from '@/src/components/helpers/base-loading/base-loading'
import CreateElements from '@/src/components/helpers/create-elements/create-elements'
import Modal from '@/src/components/helpers/overlays/modal/modal'
import { Overlay } from '@/src/components/helpers/overlays/overlay'
import BaseError from '@/src/components/helpers/base-error/base-error'
import { useVisibleCategories } from "@/src/context/product/category-product-context"
import ScrollContent from '@/src/components/helpers/scroll-content/scroll-content'
import { usePromotion } from '@/src/context/promotion/promotion-context'

/** Barra horizontal de navegação das categorias. */
export default function CategoriesNavigation() {
    const { categories, categoriesBySearch, loading, errorMessage } = useCategory()
    const { canShowPromotion } = usePromotion()
    const {
        visibleCategories,
        toggleCategoryVisibility,
        setActiveCategoryIndex,
        firstCategoryLoaded,
        setIsScrollingToSection
    } = useVisibleCategories()

    const [isModal, setIsModal] = useState(false)
    const [sectionId, setSectionId] = useState<string | null>(null)
    const [sectionIdClicked, setSectionIdClicked] = useState<string | null>(null)
    const [currentCategories, setCurrentCategories] = useState<Category.Category[]>(categories || [])

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id)

        if (element) {
            setIsScrollingToSection(true)

            const dataTarget = element.getAttribute('data-toggle')

            if (dataTarget === "off") {
                const section = document.getElementById(id)

                if (section) {
                    const headerElement = section.querySelector('.head-title') as HTMLElement | null

                    if (headerElement) {
                        const categoryId = id.replace('section-', '')

                        const categoryIndex = currentCategories.findIndex(
                            category => category.id === categoryId
                        )

                        toggleCategoryVisibility(categoryIndex)
                        headerElement.click()
                    }
                }
            }

            setTimeout(() => {
                setSectionId(null)
                setSectionIdClicked(id)
            }, 500)

            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
            const offsetPosition = elementPosition - (window.innerHeight / 6.5)

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            })

            // Detectar quando o scroll termina.
            const checkIfScrollEnded = setInterval(() => {
                const isBottomPage = window.innerHeight + window.scrollY >= document.body.offsetHeight
                const isOnElement = Number.parseInt(window.pageYOffset.toString()) === Number.parseInt(offsetPosition.toString())

                if (isOnElement || isBottomPage) {
                    setIsScrollingToSection(false)
                    clearInterval(checkIfScrollEnded)
                }
            }, 200)

            setTimeout(() => {
                setIsScrollingToSection(false)
                clearInterval(checkIfScrollEnded)
            }, 1500)

            const categoryIndex = currentCategories.findIndex(
                (category) => CategoriesFunctions.formatStrToNavigation('section', category.id) === id
            )

            if (categoryIndex !== -1) {
                if (!visibleCategories[categoryIndex]) {
                    toggleCategoryVisibility(categoryIndex)
                    setTimeout(() => {
                        setActiveCategoryIndex(categoryIndex)
                    }, 100)
                }
            }
        }
    }

    const scrollToCategory = (id: string) => {
        const element = document.getElementById(id.replace('section-', 'category-'))
        const scrollSection = document.getElementById('categories-scroll')

        if (element && scrollSection) {
            const elementPosition = element.offsetLeft - scrollSection.offsetLeft

            scrollSection.scrollTo({
                left: elementPosition - (scrollSection.clientWidth / 2) + (element.clientWidth / 2),
                behavior: 'smooth',
            })
        }
    }

    const renderHorizontalCategories = () => {
        if (loading) {
            return (
                <BaseLoading>
                    <div className={styles.loadingCategoriesArea}>
                        <CreateElements quantity={5}>
                            <aside className={styles.categoryLoading} />
                        </CreateElements>
                    </div>
                </BaseLoading>
            )
        }
        else {
            return (
                (currentCategories && currentCategories.length > 0) && currentCategories.map((item: Category.Category) => (
                    <span
                        id={CategoriesFunctions.formatStrToNavigation('category', item.id)}
                        onClick={() => scrollToSection(CategoriesFunctions.formatStrToNavigation('section', item.id))}
                        key={item.id}
                        className={`category-nav ${styles.category}`}
                        data-testid='category-nav'
                    >
                        {item.name}
                        {
                            CategoriesFunctions.formatStrToNavigation('section', item.id) === (sectionId ?? sectionIdClicked) &&
                            <div className={styles.border} />
                        }
                    </span>
                ))
            )
        }
    }

    useEffect(() => {
        if (categoriesBySearch?.categories && categoriesBySearch.categories.length > 0) {
            const _currentCategories = categoriesBySearch ? categoriesBySearch.categories.map(category => ({
                id: category.id,
                name: category.name,
                sortIndex: category.sortIndex
            })) : undefined

            setCurrentCategories(_currentCategories as Category.Category[])
        }
        else {
            setCurrentCategories(categories)
        }
    }, [categories, categoriesBySearch])

    useEffect(() => {
        const firstSectionId = document.querySelectorAll('.content-categories')[0]?.getAttribute('id')

        if (canShowPromotion)
            setSectionId('section-promocoes')
        else
            setSectionId(firstSectionId)

        setTimeout(() => {
            const sections = document.querySelectorAll('.content-categories')

            const observer = new IntersectionObserver(entries => {
                const visibleSections = entries
                    .filter(entry => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)

                if (visibleSections.length > 0) {
                    const targetSection = visibleSections[0].target
                    let id = targetSection.getAttribute("id")

                    if (id) {
                        if (!sectionIdClicked)
                            setSectionId(id)

                        scrollToCategory(id)
                    }
                }
            }, {
                rootMargin: '-30% 0px -70% 0px',
                threshold: 0
            })

            sections.forEach(section => observer.observe(section))

            const handleScroll = () => {
                if (window.innerHeight + window.scrollY >= document.body.offsetHeight) {
                    const lastSectionId = sections[sections.length - 1]?.getAttribute("id")
                    if (lastSectionId) {
                        setSectionId(lastSectionId)
                    }
                }
                if (window.scrollY <= 50) {
                    if (canShowPromotion && !categoriesBySearch?.categories) {
                        setSectionId('section-promocoes')
                    }
                    else {
                        setSectionId(firstSectionId)
                    }
                }
            }

            window.addEventListener('scroll', handleScroll)

            return () => {
                sections.forEach(section => observer.unobserve(section))
                window.removeEventListener('scroll', handleScroll)
            }
        }, 10)

        if (categoriesBySearch?.categories && categoriesBySearch.categories.length > 0) {
            setSectionId(firstSectionId)
        }
    }, [currentCategories, canShowPromotion, firstCategoryLoaded, categoriesBySearch])

    useEffect(() => {
        if (errorMessage)
            console.error(errorMessage)
    }, [errorMessage])

    if (errorMessage || (!categories || (categories && categories.length < 1) && !loading))
        return <BaseError message='Nenhum produto encontrado.' />

    if (categoriesBySearch?.categories && categoriesBySearch.categories.length < 1)
        return <></>

    return (
        <>
            {
                isModal &&
                <Modal
                    isOpen={isModal}
                    onClose={() => setIsModal(false)}
                    isInterceptRouting={false}
                    options={
                        {
                            closeButtonTheme: 'inverted',
                            headProps: {
                                elToObserveId: 'menu-category',
                                backgroundHeadTheme: 'inverted'
                            }
                        }
                    }>
                    <div className={styles.modalContainer}>
                        <div className={styles.heading} id='menu-category'>
                            <div className={styles.bar} />
                            <h2 className={styles.title}>Menu</h2>
                            <div className={styles.bar} />
                        </div>
                        <ul className={styles.items}>
                            {
                                (canShowPromotion && !categoriesBySearch?.categories) &&
                                <li
                                    onClick={() => {
                                        Overlay.dismiss()
                                        scrollToSection(CategoriesFunctions.formatStrToNavigation('section', 'promocoes'))
                                    }}
                                    className={styles.item}
                                >
                                    Promoções
                                </li>
                            }
                            {
                                currentCategories && currentCategories.map((item: Category.Category, index) => (
                                    <li
                                        onClick={() => {
                                            Overlay.dismiss()
                                            scrollToSection(CategoriesFunctions.formatStrToNavigation('section', item.id))
                                        }}
                                        className={styles.item}
                                        key={index}
                                    >
                                        {item.name}
                                    </li>
                                ))
                            }
                        </ul>
                    </div>
                </Modal>
            }
            <div className={styles.container}>
                <div onClick={() => loading ? null : setIsModal(true)} className={styles.openCategories}>
                    <SvgDots className={styles.dotsVerticalIcon} />
                </div>
                <div className={styles.categoriesScroll} >
                    <ScrollContent id='categories-scroll' removeArrows removePadding>
                        <>
                            {
                                (canShowPromotion && !categoriesBySearch?.categories) &&
                                <span
                                    id={CategoriesFunctions.formatStrToNavigation('category', 'promocoes')}
                                    onClick={() => scrollToSection(CategoriesFunctions.formatStrToNavigation('section', 'promocoes'))}
                                    className={`category-nav ${styles.category}`}
                                    data-testid='category-nav'
                                >
                                    Promoções
                                    {
                                        CategoriesFunctions.formatStrToNavigation('section', 'promocoes') === (sectionId ?? sectionIdClicked) &&
                                        <div className={styles.border} />
                                    }
                                </span>
                            }
                            {renderHorizontalCategories()}
                        </>
                    </ScrollContent>
                </div>
                <div className={styles.borderContainer}>
                    <div className={styles.borderTrack} />
                </div>
            </div>
        </>
    )
}